import type { AIChatDTO, SearchEngineResultDTO } from '@next-space/fe-api-idl';
import { BlockType, ChatStatusType, QASearchSource } from '@next-space/fe-api-idl';
import type { TableOfContentData } from '@tiptap-pro/extension-table-of-contents';
import type { Editor } from '@tiptap/core';
import dayjs from 'dayjs';
import { cloneDeep } from 'lodash-es';
import type { FC, MouseEvent, PropsWithChildren, ReactElement } from 'react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Element } from 'react-scroll';
import { Virtuoso } from 'react-virtuoso';
import { v4 } from 'uuid';
import { findAIModel } from '../../ai-const';
import { KeyboardItemType, KeyboardList } from '../../components/keyboard-list';
import { message } from '../../components/message';
import { cx } from '../../cx';
import { Icon } from '../../icon-svg/pc-icon';
import { NextModalProvider, useOpenModal } from '../../next-modal';
import { Tooltip } from '../../tooltip';
import { fastEqual } from '../../utils';
import * as clipboard from 'clipboard-polyfill';
import { DATE_FORMAT, OPEN_SPACE_AI, SIDE_BAR_WIDTH, SIDE_BAR_WIDTH_MIN } from '../const';
import { ChatContext, useConversationContext, useQuestionContext } from '../hooks/context';
import {
  getQAQueryOption,
  getRefData,
  useChat,
  useChatResultData,
  useChatStatus,
  useConversationTools,
  useSearchPageStore,
} from '../hooks/hook';
import { StateReferenceTools, type ReferenceData } from '../hooks/reference-tools';
import { getChatStatus, searchPageStore } from '../hooks/store';
import type { RefDataDTO, SaveArticleParams } from '../hooks/types';
import { ChatStatus, ChatUIStyle, SideBarType } from '../hooks/types';
import { useChatTranslation } from '../hooks/use-i18n';
import { useOpenLiteConversation } from '../hooks/use-lite-conversation-hook';
import { formatReferenceTextToMd, getSourceDisplayText, getUrlFavicon } from '../hooks/utils';
import { getAIModelItems, SourcesOption } from './chat-sources';
import { useOpenMindMap } from './mind-map';
import { RenderMarkdown } from './render-markdown';
import { SideBar } from './side-bar';
import { isMobile } from 'react-device-detect';
import { AnswerSource } from './answer-source';
import { ChatType } from '../types';
import { Button } from '../../components';
import { LoadingDots } from './loading-dots';
import { useOpenShare } from '../hooks/use-open-share';

interface ChatItemProps {
  item: AIChatDTO;
}

// #region 正文的 CHAT—ITEM
export const ChatItem: FC<ChatItemProps> = memo((props) => {
  const { item } = props;
  const { conversationId, questionId } = useQuestionContext();
  const { style } = useConversationContext();
  const [toc, setTOC] = useState<TableOfContentData>([]);

  return (
    <ChatContext.Provider
      value={{
        conversationId,
        questionId,
        chatId: item.uuid,
        toc,
        setTOC,
      }}
    >
      <NextModalProvider>
        {style === ChatUIStyle.LITE ? (
          <LiteChatItemContent item={item} />
        ) : (
          <ChatItemContent item={item} />
        )}
      </NextModalProvider>
    </ChatContext.Provider>
  );
}, fastEqual);

const ChatItemContent: FC<ChatItemProps> = memo((props) => {
  const { item } = props;
  const { functionCall } = item as any;
  const { t } = useChatTranslation();
  const {
    isFirst,
    isLast,
    bubbleList,
    handleShare,
    handleSave,
    handleCopy,
    openRetry,
    isLoading,
    isError,
  } = useChatItemHook(props);
  const { submitQuestion } = useConversationTools();

  const clickSuggestion = useCallback(
    (suggestion: string) => {
      void submitQuestion({ _keywords: suggestion });
    },
    [submitQuestion]
  );
  const hasReasoning = Boolean(item.reasoning);
  const isEmpty = !item.ai && !item.reasoning;

  return (
    <Element
      key={item.uuid}
      name={item.uuid}
      className={'flex w-full relative justify-center text-t1'}
    >
      <div className="space-y-4 w-full flex-1 max-w-2xl">
        <div hidden={isFirst} className="text-h2 break-words whitespace-pre-wrap">
          <RenderMarkdown markdown={item.user} />
          <div className="text-t2 text-grey3">{dayjs(item.createdAt).format(DATE_FORMAT)}</div>
        </div>
        <div hidden={!isFirst}>{/* 不要删掉DIV，SPACE-Y 自动有间距 */}</div>
        {/* --------------------------------------------- */}
        <div className="space-y-4">
          <h3 className="text-h3 flex items-center space-x-2 h-[52px]">
            <Icon name="IcLink" className="text-grey3" size="middle" />
            <span>{t('chatSource')}</span>
          </h3>
          <SourceGroup />
        </div>
        <div className="space-y-4">
          {/* --------------------------------------------- */}
          <h3 className="flex items-center text-h3 font-medium space-x-2 h-[52px]">
            <Icon name="IcAiFill" className="text-ai_color" size="middle" />
            <span>{t('chatAnswer')}</span>
            <AnswerSource queryOption={item.qaQueryOption} />
          </h3>
          <DeepThinking />
          <RenderMarkdown
            markdown={isError ? t('chatError') : item.ai}
            bubbleList={bubbleList}
            refResources={item.resultData}
            className={hasReasoning ? 'has-reasoning' : ''}
            functionCall={item.ai || isEmpty ? functionCall : undefined}
          />
          {/* --------------------------------------------- */}
          <div hidden={isLoading} className="flex justify-between text-t2 text-grey3">
            <div className="flex gap-4 items-center">
              <PageNumber />
              <Tooltip popup={t('chatTooltipShare')}>
                <Icon name="IcShare" size="middle" onClick={handleShare} />
              </Tooltip>
              <span
                hidden={!OPEN_SPACE_AI}
                className="flex items-center space-x-1 animate-click"
                onClick={openRetry}
              >
                <Tooltip popup={t('chatTooltipRetry')}>
                  <Icon name="IcRefresh" size="middle" />
                  <Icon name="IcArrowDown01" size="xxsmall" />
                </Tooltip>
              </span>
            </div>
            <div className="flex gap-4 items-center">
              <Tooltip popup={t('chatTooltipCopy')}>
                <Icon name="IcCopy" onClick={handleCopy} size="middle" />
              </Tooltip>
              <Tooltip popup={t('chatTooltipSaveNote')}>
                <Button
                  className="text-grey3 text-t4 h-7"
                  onClick={() => handleSave(BlockType.PAGE)}
                >
                  {t('save')}
                </Button>
              </Tooltip>
            </div>
          </div>
          {/* --------------------------------------------- */}
          <div
            hidden={!item.resultData?.suggestions?.length}
            className="w-full h-px bg-grey6 !my-6"
          />
          {/* --------------------------------------------- */}
          <div className="text-t2 text-grey2 space-y-2">
            {item.resultData?.suggestions?.map((suggestion) => (
              <div
                className="animate-click py-1 hover:text-black"
                key={suggestion}
                onClick={() => clickSuggestion(suggestion)}
              >
                {suggestion}
              </div>
            ))}
          </div>
        </div>
        <div hidden={isLast} className="w-full h-px bg-grey6" />
        <div hidden={!isLast} className="h-56" />
      </div>
      <SideBarWrapper />
    </Element>
  );
});
// #endregion

// #region 侧边栏的 CHAT—ITEM
const LiteChatItemContent: FC<ChatItemProps> = memo((props) => {
  const { item } = props;
  const { functionCall } = item as any;
  const { t } = useChatTranslation();
  const openMindMap = useOpenMindMap();
  const { insertParagraph, inWiki } = useConversationContext();
  const { resultData } = useChat();
  const refData = useMemo(() => getRefData(resultData), [resultData]);
  const { handleShare, handleSave, isError, handleCopy, openRetry, isLoading, isLast } =
    useChatItemHook(props);

  return (
    <>
      <Element name={item.uuid} id={item.uuid} className="space-y-6 text-t2">
        <div className="space-y-2">
          <div className="bg-black1/90 text-white px-2 py-1 rounded-lg rounded-br-none w-fit ml-auto ">
            <div className="py-1">{item.user}</div>
          </div>
          {item.quote && (
            <div className="text-grey3 pl-5 break-words whitespace-pre-wrap">
              {t('chatQuotePrefix')}
              {item.quote}
              {t('chatQuoteSuffix')}
            </div>
          )}
        </div>
        <div className="space-y-2">
          <LiteSourceGroup />
          <DeepThinking />
          <RenderMarkdown
            markdown={isError ? t('chatError') : item.ai}
            refResources={item.resultData}
            functionCall={item.ai ? functionCall : undefined}
          />
        </div>
        <div hidden={isLoading || !inWiki} className="flex items-center justify-between text-grey3">
          <div className="flex items-center space-x-4">
            <Tooltip popup={t('chatTooltipShare')}>
              <Icon name="IcShare" size="middle" onClick={handleShare} />
            </Tooltip>
            {OPEN_SPACE_AI && (
              <Tooltip popup={t('chatTooltipRetry')}>
                <Icon name="IcRefresh" size="middle" onClick={openRetry} />
              </Tooltip>
            )}
          </div>
          <div className="flex items-center space-x-4">
            <Tooltip popup={t('chatTooltipMindMap')}>
              <Icon name="IcMindmap" size="middle" onClick={() => openMindMap(item)} />
            </Tooltip>
            <Tooltip popup={t('chatTooltipCopy')}>
              <Icon name="IcCopy" size="middle" onClick={handleCopy} />
            </Tooltip>
            <Tooltip popup={t('chatTooltipSaveNote')}>
              <Button className="text-grey3 text-t4 h-7" onClick={() => handleSave(BlockType.PAGE)}>
                {t('save')}
              </Button>
            </Tooltip>
            {insertParagraph && (
              <Tooltip popup={t('chatTooltipInsertNote')}>
                <Icon
                  name="IcInsert"
                  size="middle"
                  onClick={() =>
                    insertParagraph({
                      ai: formatReferenceTextToMd(item.ai, refData),
                      user: item.user,
                    })
                  }
                />
              </Tooltip>
            )}
          </div>
        </div>
      </Element>
      {!isLast && <div className="h-px bg-grey6 my-6" />}
    </>
  );
});
//#endregion

const useChatItemHook = (props: ChatItemProps) => {
  const { item } = props;
  const { t } = useChatTranslation();
  const isLoading = useChatStatus(item.uuid) === ChatStatus.LOADING;
  const openRetry = useOpenRetry();
  const { updateContext, spaceId, conversationId, onSaveArticle, updateBlocks } =
    useConversationContext();
  const openShare = useOpenShare();
  const { resultData } = useChat();
  const refData = useMemo(() => getRefData(resultData), [resultData]);
  const isError = item.status === ChatStatusType.ERROR;
  const openLiteConversation = useOpenLiteConversation();

  const openSideChat = useCallback(
    (text: string) => {
      void openLiteConversation({
        keywords: text,
        parentId: conversationId,
        spaceId,
        updateBlocks,
        onOpen: (params) => {
          if (params?.conversationId) {
            updateContext({ sideBarType: SideBarType.LITE_CHAT });
          } else {
            updateContext({ sideBarType: SideBarType.LITE_CHAT_SKELETON });
          }
        },
      });
    },
    [conversationId, openLiteConversation, spaceId, updateBlocks, updateContext]
  );

  const handleShare = useCallback(
    (e: React.MouseEvent) => {
      openShare(e, {
        title: item.user,
        createdAt: item.createdAt,
        md: item.ai,
        chatId: item.uuid,
      });
    },
    [item, openShare]
  );

  const handleSave = useCallback(
    (saveType: SaveArticleParams['saveType']) => {
      onSaveArticle({ ...item, ai: formatReferenceTextToMd(item.ai, refData), saveType });
    },
    [item, onSaveArticle, refData]
  );

  const handleCopy = useCallback(() => {
    const text = formatReferenceTextToMd(item.ai, refData);
    if (text) {
      void clipboard.writeText(text);
      message.success(t('chatCopied'));
    }
  }, [item.ai, refData, t]);

  const { index, totalIndex } = useQuestionContext();
  const isFirst = index === 0;
  const isLast = index === totalIndex - 1;

  const bubbleList = useMemo(() => {
    if (isMobile) return [];
    return [
      {
        name: t('chatBubbleExplain'),
        icon: 'IcQuestion',
        iconClassName: 'text-ai_color',
        onClick: (editor: Editor) => {
          const text = editor?.state.selection.content().content.firstChild?.textContent;
          if (text) {
            openSideChat(t('chatExplainText', { text }));
          }
        },
      },
      {
        name: t('chatBubbleTakeNotes'),
        icon: 'IcEdit',
        onClick: (editor: Editor) => {
          const text = editor?.state.selection.content().content.firstChild?.textContent;
          if (text) {
            onSaveArticle({
              user: text,
              ai: '',
              saveType: BlockType.PAGE,
            });
          }
        },
      },
      {
        name: t('chatBubbleCopy'),
        icon: 'IcCopy',
        onClick: (editor: Editor) => {
          const text = editor?.state.selection.content().content.firstChild?.textContent;
          if (text) {
            message.success(t('chatCopied'));
            void clipboard.writeText(text);
          }
        },
      },
    ];
  }, [onSaveArticle, openSideChat, t]);

  return {
    isFirst,
    isLast,
    bubbleList,
    handleShare,
    handleSave,
    handleCopy,
    openRetry,
    isLoading,
    isError,
  };
};

//#region 引用资源
const getSourceImageUrl = (result: SearchEngineResultDTO) => {
  return result.img_src || getUrlFavicon(result.url);
};
interface PageIconProps {
  id: string;
}
const PageIcon: FC<PageIconProps> = (props) => {
  const { renderPageIcon } = useConversationContext();
  const block = searchPageStore((state) => state.pages[props.id]);
  if (!block) return null;

  const content = renderPageIcon?.(block);
  if (content) {
    return content as ReactElement;
  }
  return null;
};

const SourceGroup = memo(() => {
  const { t } = useChatTranslation();
  const openModal = useOpenModal();
  const refData = useChatResultData();
  const { qaQueryOption } = useChat();

  if (!refData?.length) {
    return (
      <div className="text-grey3 !mt-0">
        {t('chatAIKnowledgeSource', { model: qaQueryOption?.model || 'GPT-4' })}
      </div>
    );
  }

  const firstThreeSources = refData.slice(0, 3);

  const openAllSources = () => {
    const modalId = v4();
    openModal.modal({
      modalId,
      noAnimation: true,
      className:
        'max-w-md w-full h-full max-h-screen rounded-none animate__animated animate__fadeInRight',
      containerClassName: 'justify-end',
      content: () => (
        <div className="flex flex-col h-full">
          <div className="text-h3 font-medium flex items-center justify-between px-4 py-3">
            <div className="flex items-center space-x-2">
              <Icon name="IcLink" className="text-grey3" size="middle" />
              <span>{t('chatSource')}</span>
              <span className="text-grey3 text-t2">
                {t('chatSourceCount', { count: refData.length })}
              </span>
            </div>
            <Icon
              className="text-grey3"
              name="IcClose"
              size="middle"
              onClick={() => openModal.closeModal(modalId)}
            />
          </div>
          <div className="flex-1 overflow-hidden">
            <Virtuoso
              data={refData}
              style={{
                scrollbarWidth: 'none',
              }}
              className="w-full h-full"
              itemContent={(_, result) => (
                <div className="w-full h-full px-4 pb-4">
                  <a
                    href={result.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={t('chatOpenInNewTab')}
                    className="bg-grey9 rounded p-2 animate-click text-inherit flex flex-col h-full gap-2 ignore-security-dialog"
                  >
                    <div className="text-normal text-t2-medium flex-1 space-y-2 line-clamp-2">
                      <div>{result.title}</div>
                    </div>
                    {result.content && (
                      <div className="text-normal text-t2 flex-1 space-y-2 line-clamp-3">
                        <div>{result.content}</div>
                      </div>
                    )}
                    <div className="text-grey3 text-t4 flex items-center space-x-1 mt-auto">
                      {result.id ? (
                        <PageIcon id={result.id} />
                      ) : (
                        <img src={getSourceImageUrl(result)} className="w-4 h-4 rounded-full" />
                      )}
                      <span className="text-grey2">{getSourceDisplayText(result)}</span>
                    </div>
                  </a>
                </div>
              )}
            />
          </div>
        </div>
      ),
    });
  };

  return (
    <div className="flex items-center justify-between sm:overflow-x-auto !mt-0">
      <div className="w-full sm:w-auto gap-2 grid grid-cols-3 text-t2-medium sm:flex sm:items-center">
        {firstThreeSources.map((result, index) => (
          <a
            key={index}
            href={result.url}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-grey9 rounded-lg p-2 animate-hover text-inherit flex flex-col h-20 gap-2 sm:shrink-0 sm:w-40 ignore-security-dialog"
          >
            <div className="text-t4-medium flex-1 space-y-2 line-clamp-2">
              <div>{result.title}</div>
            </div>
            <div className="text-grey3 text-t4 flex items-center space-x-1 mt-auto h-4">
              {result.id ? (
                <PageIcon id={result.id} />
              ) : (
                <img src={getSourceImageUrl(result)} className="w-4 h-4" />
              )}
              <span>{getSourceDisplayText(result)}</span>
            </div>
          </a>
        ))}
      </div>
      <div
        hidden={refData.length < 3}
        className="h-20 shrink-0 bg-grey9 ml-2 rounded-lg p-2 animate-hover flex flex-col gap-2 justify-between w-[94px]"
        onClick={openAllSources}
      >
        <div className="flex gap-1">
          {refData.slice(3, 6).map((result, index) => {
            return result.id ? (
              <PageIcon key={index} id={result.id} />
            ) : (
              <img key={index} src={getSourceImageUrl(result)} className="w-4 h-4 rounded-full" />
            );
          })}
        </div>
        <div className="text-t4 text-grey3">{t('chatViewAllSources')}</div>
      </div>
    </div>
  );
});

const LiteSourceGroup = memo(() => {
  const { t } = useChatTranslation();
  const refData = useChatResultData();
  const [show, setShow] = useState(false);

  if (!refData?.length) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div
        className="text-t1-medium flex items-center justify-between animate-click"
        onClick={() => setShow(!show)}
      >
        <span>{t('chatSourcesCited', { count: refData.length })}</span>
        <Icon name={show ? 'IcArrowUp01' : 'IcArrowDown01'} size="xsmall" />
      </div>
      {show && (
        <div className="space-y-2 animate-fade-in bg-grey9 p-4 rounded-md text-t2">
          {refData.map((result, index) => (
            <a
              href={result.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-t2 flex items-center space-x-1"
            >
              <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 bg-grey6 text-grey1 rounded-full">
                {index + 1}
              </span>
              <span className="truncate">{result.title}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
});
//#endregion

//#region 侧边栏
const SideBarWrapper = memo(() => {
  const { sideBarType } = useConversationContext();

  if (sideBarType !== SideBarType.OUTLINE) {
    return null;
  }
  if (isMobile) return null;
  return (
    <div
      className="sticky right-0 self-start top-0 flex-shrink-0 px-10 max-w-full"
      style={{ width: SIDE_BAR_WIDTH, minWidth: SIDE_BAR_WIDTH_MIN }}
    >
      <SideBar />
    </div>
  );
});
//#endregion

//#region 重试
const useNewStateReference = (props?: { onChange?: (data: ReferenceData) => void }) => {
  const chat = useChat();
  const initialState = useMemo(
    () =>
      cloneDeep({
        status: getChatStatus(chat.uuid),
        qaQueryOption: getQAQueryOption(chat.qaQueryOption),
      }),
    [chat]
  );

  const [state, setState] = useState<ReferenceData>(initialState);

  const tools = useMemo(
    () =>
      StateReferenceTools.create(v4(), state, (data) => {
        setState(data);
        props?.onChange?.(data);
      }),
    [props, state]
  );

  return { state, setState, tools };
};

const retryModalId = v4();
const useOpenRetry = () => {
  const openModal = useOpenModal();

  return useCallback(
    (event: MouseEvent) => {
      openModal.dropdown({
        modalId: retryModalId,
        popcorn: event.currentTarget,
        content: () => <RetryContent />,
      });
    },
    [openModal]
  );
};

const RetryContent = () => {
  const openModal = useOpenModal();
  const chat = useChat();
  const conversationTools = useConversationTools();
  const { spaceId, inWiki } = useConversationContext();
  const { questionId } = useQuestionContext();

  const { t } = useChatTranslation();

  const retry = useCallback(
    (data: ReferenceData) => {
      void conversationTools.createQuestion({
        ...data,
        keywords: chat.user,
        spaceId,
        questionId,
      });
      openModal.closeModal(retryModalId);
    },
    [chat, conversationTools, openModal, questionId, spaceId]
  );
  const { state, tools } = useNewStateReference({
    onChange: retry,
  });

  const aiModelItems = useMemo(() => {
    return getAIModelItems(state.qaQueryOption.model);
  }, [state.qaQueryOption.model]);

  const openSourcesOption = useCallback(
    (event: React.MouseEvent) => {
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
      openModal.dropdown({
        popcorn: {
          getBoundingClientRect: () => rect,
        },
        placement: 'right',
        offset: [-20, 10],
        content: () => <RetrySourcesOption retry={retry} />,
      });
    },
    [openModal, retry]
  );
  const searchSource = inWiki
    ? []
    : [
        {
          type: KeyboardItemType.Divider,
        },
        {
          title: t('retry_source'),
          type: KeyboardItemType.Divider,
        },
        {
          title: t('chatWebSearch'),
          type: KeyboardItemType.Default,
          isChecked: conversationTools.chatType === ChatType.SEARCH,
          onClick: () => {
            tools.updateQaQueryOption({
              networkSearch: true,
              qaSearchSource: QASearchSource.AI_MODEL,
            });
          },
        },
        {
          title: t('findInAnotherSource'),
          type: KeyboardItemType.Default,
          isChecked: conversationTools.chatType === ChatType.SPACE,
          onClick: openSourcesOption,
        },
        {
          title: t('ai_model_tip'),
          type: KeyboardItemType.Default,
          isChecked: conversationTools.chatType === ChatType.AI_MODAL,
          onClick: () => {
            tools.updateQaQueryOption({
              networkSearch: false,
              qaSearchSource: QASearchSource.AI_MODEL,
            });
          },
        },
      ];

  return (
    <div className="next-modal py-1">
      <KeyboardList
        items={[
          {
            title: t('retry_ai_model'),
            type: KeyboardItemType.Divider,
          },
          ...aiModelItems,

          ...searchSource,
        ]}
        onClickItem={(item) => {
          if (item.aiModel) {
            tools.updateQaQueryOption({
              model: item.id,
            });
          }
          openModal.closeModal(retryModalId);
        }}
      />
    </div>
  );
};

const RetrySourcesOption = (props: { retry: (data: ReferenceData) => void }) => {
  const retryFunRef = useRef<typeof props.retry>();
  retryFunRef.current = props.retry;
  const stateRef = useRef<ReferenceData>();

  const { state, tools } = useNewStateReference({
    onChange: (state) => {
      stateRef.current = state;
    },
  });
  //由于可以选中多个页面,因此只有state有变动且对话框消失才会去重新回答
  useEffect(() => {
    return () => {
      if (stateRef.current) {
        retryFunRef.current?.({
          ...stateRef.current,
          qaQueryOption: { ...stateRef.current.qaQueryOption, networkSearch: false },
        });
      }
    };
  }, []);
  return <SourcesOption {...state} {...tools} />;
};

//#endregion

//#region 页码
const PageNumber = () => {
  const { curChatIndex, totalChats, handlePrevious, handleNext } = useQuestionContext();

  if (totalChats <= 1) {
    return null;
  }

  return (
    <span className="flex items-center space-x-2 text-t4">
      <span className="flex items-center text-t4">
        <Icon
          name="IcArrowLeft01"
          size="xxsmall"
          className={cx(
            'cursor-pointer',
            curChatIndex === 0 ? 'text-grey3 cursor-not-allowed' : 'text-normal animate-hover'
          )}
          onClick={curChatIndex === 0 ? undefined : handlePrevious}
        />
      </span>
      <span className="text-t2 text-grey3">{`${curChatIndex + 1}/${totalChats}`}</span>
      <span className="flex items-center text-t4">
        <Icon
          name="IcArrowRight01"
          size="xxsmall"
          className={cx(
            'cursor-pointer',
            curChatIndex === totalChats - 1
              ? 'text-grey3 cursor-not-allowed'
              : 'text-normal animate-hover'
          )}
          onClick={curChatIndex === totalChats - 1 ? undefined : handleNext}
        />
      </span>
    </span>
  );
};
//#endregion

const DeepThinking = memo(() => {
  const { t } = useChatTranslation();
  const [showDeepThinking, setShowDeepThinking] = useState(true);
  const item = useChat();
  const hasReasoning = Boolean(item.reasoning);
  //@ts-ignore typecheck
  const { functionCall } = item;

  return (
    <div className="space-y-2 !mt-0 pb-2" hidden={!hasReasoning}>
      <div
        className="text-t2 flex items-center space-x-2 animate-click text-grey3 py-2"
        onClick={() => setShowDeepThinking(!showDeepThinking)}
      >
        <span>{t('deepThinking')}</span>
        <Icon
          name="IcArrowRight01"
          size="xxsmall"
          className={cx('transition-all', showDeepThinking && 'rotate-90')}
        />
      </div>
      <RenderMarkdown
        className={'reason-markdown-body border-l-2 pl-4 text-grey3 animate-fade-in text-t5'}
        markdown={item.reasoning}
        refResources={item.resultData}
        hidden={!showDeepThinking}
        showToc={false}
      />
      {!item.ai && <LoadingDots functionCall={functionCall} />}
    </div>
  );
});
